import { Clock, MessageSquare, TicketIcon, UserCheck, TrendingUp, Timer, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  trend?: number;
  colorClass?: string;
  bgClass?: string;
  delay?: string;
}

const StatCard = ({ title, value, description, icon, trend, colorClass, bgClass, delay }: StatCardProps) => {
  return (
    <div className={cn("rounded-2xl p-6 border border-border/50 shadow-sm opacity-0 animate-fadeIn bg-card", delay)}>
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
          <h3 className="text-3xl font-extrabold mt-2 tracking-tight">{value}</h3>
          <p className="text-sm text-muted-foreground mt-1 truncate">{description}</p>
          {trend !== undefined && (
            <div className={cn("inline-flex items-center gap-1 text-xs font-semibold mt-2 px-2 py-0.5 rounded-full", trend > 0 ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600")}>
              <TrendingUp className={cn("h-3 w-3", trend < 0 && "rotate-180")} />
              {trend > 0 ? "+" : ""}{trend}% vs mês anterior
            </div>
          )}
        </div>
        <div className={cn("p-3 rounded-2xl shrink-0 ml-4", bgClass || "bg-primary/10")}>
          <div className={colorClass || "text-primary"}>{icon}</div>
        </div>
      </div>
    </div>
  );
};

interface DashboardStatsProps {
  openTickets?: number;
  totalTickets?: number;
  totalMessages?: number;
  totalClients?: number;
  slaFirstResponse?: { compliance: number; total: number; met: number } | null;
  avgFirstResponseMinutes?: number | null;
  isLoading?: boolean;
}

export default function DashboardStats({
  openTickets = 0,
  totalTickets = 0,
  totalMessages = 0,
  totalClients = 0,
  slaFirstResponse,
  avgFirstResponseMinutes,
  isLoading,
}: DashboardStatsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-2xl border border-border/50 p-6 bg-card animate-pulse">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="h-3 bg-muted rounded-full w-2/3 mb-3" />
                <div className="h-8 bg-muted rounded-lg w-1/2 mb-2" />
                <div className="h-3 bg-muted rounded-full w-3/4" />
              </div>
              <div className="h-12 w-12 bg-muted rounded-2xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard
        title="Tickets Abertos"
        value={openTickets}
        description="Aguardando atendimento"
        icon={<TicketIcon className="h-6 w-6" />}
        colorClass="text-primary"
        bgClass="bg-primary/10"
        delay="animate-delay-100"
      />
      <StatCard
        title="Total de Tickets"
        value={totalTickets}
        description="Todos os tickets"
        icon={<Clock className="h-6 w-6" />}
        colorClass="text-orange-500"
        bgClass="bg-orange-500/10"
        delay="animate-delay-200"
      />
      <StatCard
        title="Mensagens"
        value={totalMessages.toLocaleString("pt-BR")}
        description="Total nos tickets"
        icon={<MessageSquare className="h-6 w-6" />}
        colorClass="text-blue-500"
        bgClass="bg-blue-500/10"
        delay="animate-delay-300"
      />
      <StatCard
        title="Clientes"
        value={totalClients.toLocaleString("pt-BR")}
        description="Clientes cadastrados"
        icon={<UserCheck className="h-6 w-6" />}
        colorClass="text-emerald-500"
        bgClass="bg-emerald-500/10"
        delay="animate-delay-400"
      />
      <StatCard
        title="SLA de 1ª resposta"
        value={slaFirstResponse ? `${slaFirstResponse.compliance}%` : "—"}
        description={
          slaFirstResponse
            ? `${slaFirstResponse.met} de ${slaFirstResponse.total} dentro do prazo`
            : "Nenhuma SLA de resposta configurada"
        }
        icon={<ShieldCheck className="h-6 w-6" />}
        colorClass="text-violet-500"
        bgClass="bg-violet-500/10"
        delay="animate-delay-500"
      />
      <StatCard
        title="Tempo médio de 1ª resposta"
        value={avgFirstResponseMinutes != null ? formatMinutes(avgFirstResponseMinutes) : "—"}
        description="Do ticket aberto até o agente responder"
        icon={<Timer className="h-6 w-6" />}
        colorClass="text-rose-500"
        bgClass="bg-rose-500/10"
        delay="animate-delay-600"
      />
    </div>
  );
}
